import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type PermissionInfo, type RoleSummary } from '@/lib/adminApi';
import { Badge, Card, DataTable, EmptyState, Spinner, StatTile, type Column } from '@/components/ui';
import { IconShield, IconUsers } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { Drawer } from '@/components/admin/Drawer';
import { pagesForPermission } from '@/components/admin/adminNav';
import { useAdminPermission } from '@/lib/useAdminPermission';
import {
  describeAccess,
  groupByArea,
  isCritical,
  listSentence,
  permissionMeta,
  roleBlurb,
} from '@/lib/permissionCatalog';

/** Roles — the access-control screen. Staff land here to answer three
 *  questions: who can do what, is anything over-permissioned, and how do I
 *  create a role for a new team. The four built-in roles are read-only (they
 *  are a matrix in admin-svc); custom roles are authored here.
 *
 *  Requires staff.manage to edit — the nav entry, this page and admin-svc all
 *  gate on it. Without it the page is still readable, which is deliberate:
 *  reviewing access shouldn't require the power to change it. */
export function Roles() {
  const canManage = useAdminPermission('staff.manage');
  const [q, setQ] = useState('');
  /** null = closed. '' = the create form. Otherwise the role being opened. */
  const [editing, setEditing] = useState<string | null>(null);
  /** Seeds the create form when duplicating an existing role. */
  const [seedFrom, setSeedFrom] = useState<string | null>(null);
  const qc = useQueryClient();

  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: ({ signal }) => adminApi.roles(signal),
  });
  const permsQuery = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: ({ signal }) => adminApi.permissions(signal),
  });

  const roles = rolesQuery.data?.items ?? [];
  const catalog = permsQuery.data?.permissions ?? [];

  // Pull each role's permission set so the list can summarise real access
  // rather than a bare number. The set is small (four built-ins plus however
  // many custom roles exist) and every result lands in the SAME cache key the
  // drawer reads, so opening a role afterwards is instant.
  const details = useQueries({
    queries: roles.map((r) => ({
      queryKey: ['admin', 'role', r.name],
      queryFn: ({ signal }: { signal?: AbortSignal }) => adminApi.role(r.name, signal),
      staleTime: 30_000,
    })),
  });
  const permsByRole = new Map<string, string[]>();
  roles.forEach((r, i) => {
    const perms = details[i]?.data?.role?.permissions;
    if (perms) permsByRole.set(r.name, perms);
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return roles;
    return roles.filter(
      (r) =>
        r.name.toLowerCase().includes(needle) ||
        roleBlurb(r.name, r.description, r.is_system).toLowerCase().includes(needle),
    );
  }, [roles, q]);

  const customCount = roles.filter((r) => !r.is_system).length;
  const staffCovered = roles.reduce((sum, r) => sum + r.staff_count, 0);

  const columns: Array<Column<RoleSummary>> = [
    {
      key: 'name',
      header: 'Role',
      cell: (r) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-100">{r.name}</span>
            {r.is_system ? <Badge tone="neutral">Built-in</Badge> : <Badge tone="info">Custom</Badge>}
          </div>
          <p className="mt-0.5 max-w-md truncate text-[12px] text-text-secondary">
            {roleBlurb(r.name, r.description, r.is_system) || 'No description'}
          </p>
        </div>
      ),
    },
    {
      key: 'access',
      header: 'Access',
      cell: (r) => {
        const perms = permsByRole.get(r.name);
        if (!perms) return <span className="text-xs text-muted">…</span>;
        const criticals = perms.filter(isCritical).length;
        return (
          <div className="min-w-0">
            <div className="text-[13px] text-slate-200">{describeAccess(perms, catalog.length)}</div>
            {criticals > 0 ? (
              <div className="mt-0.5 text-[12px] text-warn">
                {criticals} sensitive {criticals === 1 ? 'permission' : 'permissions'}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'staff',
      header: 'Assigned',
      cell: (r) =>
        r.staff_count === 0 ? (
          <span className="text-xs text-muted">Nobody</span>
        ) : (
          <span className="text-[13px] text-slate-200">
            {r.staff_count} {r.staff_count === 1 ? 'person' : 'people'}
          </span>
        ),
    },
    {
      key: 'manage',
      header: '',
      align: 'right',
      cell: (r) => (
        <div className="flex justify-end gap-2">
          {canManage ? (
            <button
              type="button"
              onClick={() => {
                setSeedFrom(r.name);
                setEditing('');
              }}
              className="rounded-md border border-ink-700 px-2 py-1 text-xs text-slate-300 hover:bg-hover"
              title={`Create a new role starting from ${r.name}`}
            >
              Duplicate
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setSeedFrom(null);
              setEditing(r.name);
            }}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
          >
            {r.is_system || !canManage ? 'View' : 'Edit'}
          </button>
        </div>
      ),
    },
  ];

  const onSaved = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
    qc.invalidateQueries({ queryKey: ['admin', 'role'] });
    setEditing(null);
    setSeedFrom(null);
  };

  return (
    <AdminPage>
      <AdminPageHeader
        title="Roles"
        subtitle="What each staff member can see and do. Built-in roles cover most teams; create your own when they don't."
        right={
          <div className="flex items-center gap-2">
            <SearchBox value={q} onChange={setQ} placeholder="Search roles…" />
            {canManage ? (
              <button
                type="button"
                onClick={() => {
                  setSeedFrom(null);
                  setEditing('');
                }}
                className="btn-primary shrink-0"
              >
                New role
              </button>
            ) : null}
          </div>
        }
      />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile label="Roles" value={roles.length} hint={`${customCount} custom`} icon={<IconShield className="h-4 w-4" />} />
        <StatTile
          label="Staff assigned"
          value={staffCovered}
          hint={staffCovered === 0 ? 'No staff yet' : 'Across all roles'}
          icon={<IconUsers className="h-4 w-4" />}
        />
        <StatTile
          label="Permissions available"
          value={catalog.length || '—'}
          hint="Everything a role can be granted"
        />
      </div>

      {rolesQuery.data && !rolesQuery.data.available ? (
        <Card className="mb-4 border-warn/30">
          <p className="text-sm text-warn">
            Custom roles aren't switched on for this environment yet. The built-in roles below work as normal, and
            authoring will appear here once it's enabled.
          </p>
        </Card>
      ) : null}

      <Card flush>
        {rolesQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading roles…" />
          </div>
        ) : rolesQuery.isError ? (
          <EmptyState title="Couldn't load roles" description={(rolesQuery.error as Error)?.message} />
        ) : (
          <DataTable<RoleSummary>
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.name}
            stickyHeader
            empty={
              <EmptyState
                title={q ? 'No roles match that search' : 'No roles yet'}
                description={q ? 'Try a different name.' : undefined}
                icon={<IconShield className="h-6 w-6" />}
              />
            }
          />
        )}
      </Card>

      {editing !== null ? (
        <RoleDrawer
          name={editing}
          seedFrom={seedFrom}
          canManage={canManage}
          catalog={catalog}
          onClose={() => {
            setEditing(null);
            setSeedFrom(null);
          }}
          onSaved={onSaved}
        />
      ) : null}
    </AdminPage>
  );
}

// ─── role drawer ─────────────────────────────────────────────────────────────

function RoleDrawer({
  name,
  seedFrom,
  canManage,
  catalog,
  onClose,
  onSaved,
}: {
  name: string;
  seedFrom: string | null;
  canManage: boolean;
  catalog: PermissionInfo[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isCreate = name === '';

  // Edit: the role being changed. Create-by-duplicate: the role we're copying.
  const sourceName = isCreate ? seedFrom : name;
  const { data: roleData, isLoading } = useQuery({
    queryKey: ['admin', 'role', sourceName],
    queryFn: ({ signal }) => adminApi.role(sourceName as string, signal),
    enabled: !!sourceName,
  });
  const role = roleData?.role;
  const isSystem = !isCreate && (role?.is_system ?? false);
  const readOnly = !canManage || isSystem;

  const heading = isCreate ? (seedFrom ? `New role from ${seedFrom}` : 'New role') : role?.name ?? name;
  const sub = isCreate
    ? 'Name it, then pick exactly what it can reach.'
    : isSystem
      ? "Built-in role — its permissions are fixed"
      : 'Change what this role can reach';

  return (
    <Drawer title={heading} subtitle={sub} onClose={onClose} width="wide">
      {sourceName && isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading role…" />
        </div>
      ) : (
        <RoleForm
          key={`${name}:${seedFrom ?? ''}`}
          isCreate={isCreate}
          duplicatedFrom={isCreate ? seedFrom : null}
          readOnly={readOnly}
          isSystem={isSystem}
          existingName={isCreate ? '' : role?.name ?? name}
          initialDescription={isCreate && seedFrom ? '' : role?.description ?? ''}
          initialPermissions={role?.permissions ?? []}
          catalog={catalog}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    </Drawer>
  );
}

/** A checkbox that can show a third "some selected" state. */
function TriCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className="h-4 w-4 rounded border-ink-700 bg-ink-850 text-brand-500 focus:ring-brand-500 disabled:opacity-50"
    />
  );
}

function RoleForm({
  isCreate,
  duplicatedFrom,
  readOnly,
  isSystem,
  existingName,
  initialDescription,
  initialPermissions,
  catalog,
  onClose,
  onSaved,
}: {
  isCreate: boolean;
  duplicatedFrom: string | null;
  readOnly: boolean;
  isSystem: boolean;
  existingName: string;
  initialDescription: string;
  initialPermissions: string[];
  catalog: PermissionInfo[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(duplicatedFrom ? `${duplicatedFrom}-copy` : '');
  const [description, setDescription] = useState(initialDescription);
  const [selected, setSelected] = useState<Set<string>>(new Set(initialPermissions));
  const [filter, setFilter] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Dirty tracking so the footer can show unsaved state and the Cancel button
  // can warn rather than silently discarding a half-built role.
  const initialSet = useMemo(() => new Set(initialPermissions), [initialPermissions]);
  const permsChanged =
    selected.size !== initialSet.size || [...selected].some((p) => !initialSet.has(p));
  const dirty = permsChanged || description !== initialDescription || (isCreate && name.trim() !== '');

  // Filter the catalogue by label/description/slug, then group into areas.
  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return catalog;
    return catalog.filter((p) => {
      const meta = permissionMeta(p.permission);
      // Match the MENU NAME too, so searching "operations" finds the
      // permission that unlocks the Operations page even though its label
      // talks about feature settings.
      const pages = pagesForPermission(p.permission).join(' ').toLowerCase();
      return (
        meta.label.toLowerCase().includes(needle) ||
        meta.description.toLowerCase().includes(needle) ||
        pages.includes(needle) ||
        p.permission.toLowerCase().includes(needle)
      );
    });
  }, [catalog, filter]);
  const groups = useMemo(() => groupByArea(visible), [visible]);

  const toggle = (perm: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return next;
    });

  const setMany = (perms: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of perms) {
        if (on) next.add(p);
        else next.delete(p);
      }
      return next;
    });

  const grantedCriticals = [...selected].filter(isCritical);

  const save = useMutation({
    mutationFn: () =>
      isCreate
        ? adminApi.createRole({
            name: name.trim(),
            description: description.trim(),
            permissions: [...selected],
          })
        : adminApi.updateRole(existingName, {
            description: description.trim(),
            permissions: [...selected],
          }),
    onSuccess: onSaved,
  });

  const del = useMutation({
    mutationFn: () => adminApi.deleteRole(existingName),
    onSuccess: onSaved,
  });

  const trimmed = name.trim();
  const nameValid = /^[a-z0-9][a-z0-9_-]{1,39}$/.test(trimmed);
  const nameError =
    isCreate && trimmed !== '' && !nameValid
      ? 'Use 2–40 characters: lowercase letters, numbers, hyphen or underscore.'
      : null;
  const canSave = !readOnly && (isCreate ? nameValid && selected.size > 0 : dirty);

  return (
    <>
      <div className="space-y-4 pb-2">
        {isSystem ? (
          <Card className="border-ink-700">
            <p className="text-[13px] text-text-secondary">
              This role is built into Telride and can't be edited. To grant a different mix of access, duplicate it and
              adjust the copy.
            </p>
          </Card>
        ) : null}

        {/* Identity */}
        <Card>
          <div className="space-y-3">
            {isCreate ? (
              <div>
                <label htmlFor="role-name" className="block text-[12px] font-medium text-slate-300">
                  Role name
                </label>
                <input
                  id="role-name"
                  value={name}
                  onChange={(e) => setName(e.target.value.toLowerCase())}
                  placeholder="dispatch-lead"
                  autoFocus
                  className="mt-1 input"
                />
                {nameError ? (
                  <p className="mt-1 text-xs text-danger">{nameError}</p>
                ) : (
                  <p className="mt-1 text-xs text-muted">
                    Staff see this name on their profile. It can't be changed later.
                  </p>
                )}
              </div>
            ) : null}
            <div>
              <label htmlFor="role-desc" className="block text-[12px] font-medium text-slate-300">
                Purpose
              </label>
              <input
                id="role-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Who this role is for and what they handle"
                disabled={readOnly}
                className="mt-1 input"
              />
              <p className="mt-1 text-xs text-muted">
                Shown wherever this role is picked, so whoever assigns it knows what they're granting.
              </p>
            </div>
          </div>
        </Card>

        {/* Permission picker */}
        <Card>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-[13px] font-semibold text-slate-100">Permissions</div>
              <p className="text-xs text-text-secondary">
                {selected.size === 0
                  ? 'Nothing selected yet'
                  : describeAccess([...selected], catalog.length)}
              </p>
            </div>
            {!readOnly ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setMany(
                      catalog.filter((p) => permissionMeta(p.permission).risk === 'view').map((p) => p.permission),
                      true,
                    )
                  }
                  className="rounded-md border border-ink-700 px-2 py-1 text-xs text-slate-300 hover:bg-hover"
                >
                  Add all view-only
                </button>
                <button
                  type="button"
                  disabled={selected.size === 0}
                  onClick={() => setSelected(new Set())}
                  className="rounded-md border border-ink-700 px-2 py-1 text-xs text-slate-300 hover:bg-hover disabled:opacity-40"
                >
                  Clear
                </button>
              </div>
            ) : null}
          </div>

          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter permissions…"
            className="input mb-3"
          />

          <div className="space-y-5">
            {groups.map((group) => {
              const perms = group.items.map((i) => i.permission);
              const on = perms.filter((p) => selected.has(p)).length;
              const all = on === perms.length && perms.length > 0;
              return (
                <div key={group.key}>
                  <div className="mb-2 flex items-start gap-2 border-b border-ink-800 pb-1.5">
                    {!readOnly ? (
                      <div className="pt-0.5">
                        <TriCheckbox
                          checked={all}
                          indeterminate={on > 0}
                          onChange={() => setMany(perms, !all)}
                          label={`Select all in ${group.label}`}
                        />
                      </div>
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-slate-100">{group.label}</span>
                        <span className="metric text-[11px] text-muted">
                          {on}/{perms.length}
                        </span>
                      </div>
                      <p className="text-[11px] text-text-muted">{group.blurb}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 lg:grid-cols-2">
                    {group.items.map((p) => {
                      const meta = permissionMeta(p.permission);
                      const checked = selected.has(p.permission);
                      return (
                        <label
                          key={p.permission}
                          className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 transition-colors ${
                            readOnly ? 'cursor-default' : 'hover:bg-hover'
                          }`}
                          title={p.permission}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={readOnly}
                            onChange={() => toggle(p.permission)}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-ink-700 bg-ink-850 text-brand-500 focus:ring-brand-500 disabled:opacity-50"
                          />
                          <span className="min-w-0">
                            <span className="flex flex-wrap items-center gap-1.5">
                              <span className="text-[13px] text-slate-200">{meta.label}</span>
                              {meta.risk === 'critical' ? <Badge tone="warn">Sensitive</Badge> : null}
                              {/* Which sidebar item this unlocks — the way an
                                  operator actually thinks about access. */}
                              {pagesForPermission(p.permission).map((page) => (
                                <Badge key={page} tone="neutral">{page}</Badge>
                              ))}
                            </span>
                            <span className="block text-[11px] leading-snug text-text-muted">{meta.description}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {groups.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">No permissions match “{filter}”.</p>
            ) : null}
          </div>
        </Card>

        {/* What this role can do that matters */}
        {grantedCriticals.length > 0 ? (
          <Card className="border-warn/30">
            <div className="text-[13px] font-semibold text-slate-100">Sensitive access</div>
            <p className="mt-0.5 text-xs text-text-secondary">
              Anyone with this role will be able to {listSentence(
                grantedCriticals.map((p) => permissionMeta(p).label.toLowerCase()),
              )}
              .
            </p>
          </Card>
        ) : null}
      </div>

      {/* Sticky actions */}
      {!readOnly ? (
        <RoleFooter
          isCreate={isCreate}
          dirty={dirty}
          canSave={canSave}
          saving={save.isPending}
          deleting={del.isPending}
          confirmingDelete={confirmingDelete}
          setConfirmingDelete={setConfirmingDelete}
          onSave={() => save.mutate()}
          onDelete={() => del.mutate()}
          onClose={onClose}
          error={((save.error as Error) || (del.error as Error))?.message}
        />
      ) : null}
    </>
  );
}

/** The drawer's action bar. Rendered inside RoleForm rather than via Drawer's
 *  footer prop because it needs the form's dirty/mutation state. */
function RoleFooter({
  isCreate,
  dirty,
  canSave,
  saving,
  deleting,
  confirmingDelete,
  setConfirmingDelete,
  onSave,
  onDelete,
  onClose,
  error,
}: {
  isCreate: boolean;
  dirty: boolean;
  canSave: boolean;
  saving: boolean;
  deleting: boolean;
  confirmingDelete: boolean;
  setConfirmingDelete: (v: boolean) => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
  error?: string;
}) {
  return (
    <div className="sticky bottom-0 -mx-5 mt-2 border-t border-ink-700 bg-ink-900/95 px-5 py-3 backdrop-blur">
      {error ? <p className="mb-2 text-xs text-danger">{error}</p> : null}
      {confirmingDelete ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[13px] text-slate-200">
            Delete this role? Staff who hold it will need a new one before they can sign in.
          </span>
          <button
            type="button"
            disabled={deleting}
            onClick={onDelete}
            className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm font-semibold text-danger hover:bg-danger/20 disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : 'Yes, delete'}
          </button>
          <button type="button" onClick={() => setConfirmingDelete(false)} className="btn-secondary">
            Keep it
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <div>
            {!isCreate ? (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="rounded-lg px-2 py-2 text-sm font-medium text-danger hover:bg-danger/10"
              >
                Delete role
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {dirty ? <span className="text-xs text-muted">Unsaved changes</span> : null}
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="button" disabled={!canSave || saving} onClick={onSave} className="btn-primary">
              {saving ? 'Saving…' : isCreate ? 'Create role' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
